import { Button, Modal } from 'antd';
import { useRef, useState } from 'react';
import type { ProdutoSelecionado } from '@/features/produto/types/ProdutoSelecionado';
import type { ProdutoEstoqueRegistro } from '@/features/produto/util/estoque';
import type { Produto } from '../../types/Produto';
import type { ProdutoLote } from '../../types/ProdutoLote';
import type { ProdutoPacoteBarra } from '../../types/ProdutoPacoteBarra';
import { ProdutoSelectModalLocal } from './ProdutoSelectModalLocal';
import { ProdutoSelectModalLote } from './ProdutoSelectModalLote';
import { ProdutoSelectModalPacote } from './ProdutoSelectModalPacote';
import { ProdutoSelectModalProduto } from './ProdutoSelectModalProduto';
import { ProdutoSelectModalSteps, Step } from './ProdutoSelectModalSteps';
import {
    getProdutoSelectEstoques,
    getProdutoSelectLocalAutomatico,
    getProdutoSelectLoteAutomatico,
    produtoTemEtapaLote,
} from './produtoSelectUtil';

const titulos: Record<Step, string> = {
    [Step.Produto]: 'Dados do produto',
    [Step.PacoteBarra]: 'Pacote / Barra',
    [Step.Lote]: 'Lote',
    [Step.LocalEstoque]: 'Local de estoque',
};

export function ProdutoSelectModal({
    produto,
    tabelaPrecoIdpk,
    onClose,
    onSelect,
}: {
    produto: Produto;
    tabelaPrecoIdpk?: number;
    onClose: () => void;
    onSelect: (args: ProdutoSelecionado) => void;
}) {
    const temPacote = !!produto.produto_pacote_barra?.length;
    const temLote = produtoTemEtapaLote(produto);

    const primeiraEtapaSelecao = temPacote
        ? Step.PacoteBarra
        : temLote
          ? Step.Lote
          : Step.LocalEstoque;

    const [etapa, setEtapa] = useState<Step>(primeiraEtapaSelecao);
    const [pacoteBarra, setPacoteBarra] = useState<ProdutoPacoteBarra | null>(null);
    const [pacoteEmFoco, setPacoteEmFoco] = useState<ProdutoPacoteBarra | null>(null);
    const [lote, setLote] = useState<ProdutoLote | null>(() =>
        getProdutoSelectLoteAutomatico(produto),
    );
    const [loteEmFoco, setLoteEmFoco] = useState<ProdutoLote | null>(null);
    const [localEstoque, setLocalEstoque] = useState<ProdutoEstoqueRegistro | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);

    const loteAtual = etapa === Step.Lote ? loteEmFoco : lote;
    const estoques = getProdutoSelectEstoques(produto, loteAtual);

    const localSelecionado =
        localEstoque ?? getProdutoSelectLocalAutomatico(produto, pacoteBarra, loteAtual);

    const pacoteAtual = etapa === Step.PacoteBarra ? pacoteEmFoco : pacoteBarra;

    const precisaLocal =
        !getProdutoSelectLocalAutomatico(produto, pacoteAtual, loteAtual) && estoques.length > 1;

    const ultimoPasso =
        etapa === Step.LocalEstoque ||
        (etapa === Step.Lote && !precisaLocal) ||
        (etapa === Step.PacoteBarra && !temLote && !precisaLocal);

    const semOpcoes =
        etapa === Step.Lote
            ? !produto.lote?.length
            : etapa === Step.LocalEstoque && !estoques.length;

    const etapaAnterior =
        etapa === Step.LocalEstoque && temLote
            ? Step.Lote
            : etapa > Step.PacoteBarra && temPacote
              ? Step.PacoteBarra
              : Step.Produto;

    const selecionarEstoqueOuConcluir = (
        pacoteSelecionado: ProdutoPacoteBarra | null,
        loteSelecionado: ProdutoLote | null,
    ): void => {
        const local = getProdutoSelectLocalAutomatico(produto, pacoteSelecionado, loteSelecionado);

        if (!local && getProdutoSelectEstoques(produto, loteSelecionado).length > 1) {
            setEtapa(Step.LocalEstoque);
            return;
        }

        setLocalEstoque(local);

        onSelect({
            localEstoque: local,
            lote: loteSelecionado,
            pacoteBarra: pacoteSelecionado,
            produto,
        });
    };

    return (
        <Modal
            afterOpenChange={(open) => {
                if (open) {
                    containerRef.current
                        ?.querySelector<HTMLElement>('[data-item-selected="true"]')
                        ?.focus();
                }
            }}
            footer={
                etapa === Step.Produto ? (
                    <Button onClick={() => setEtapa(primeiraEtapaSelecao)} type='primary'>
                        Avançar
                    </Button>
                ) : (
                    <>
                        <Button onClick={() => setEtapa(etapaAnterior)}>Voltar</Button>
                        {ultimoPasso && (
                            <Button
                                disabled={semOpcoes}
                                onClick={() =>
                                    containerRef.current
                                        ?.querySelector<HTMLElement>('[data-item-selected="true"]')
                                        ?.click()
                                }
                                type='primary'
                            >
                                OK
                            </Button>
                        )}
                    </>
                )
            }
            onCancel={onClose}
            open={true}
            title={titulos[etapa]}
            width={900}
        >
            <ProdutoSelectModalSteps
                localEstoque={localSelecionado}
                lote={lote}
                onChange={setEtapa}
                pacoteBarra={pacoteBarra}
                produto={produto}
                showLocalEstoque={!localSelecionado && estoques.length > 1}
                step={etapa}
            />

            <div key={etapa} ref={containerRef}>
                {etapa === Step.Produto && <ProdutoSelectModalProduto produto={produto} />}

                {etapa === Step.PacoteBarra && (
                    <ProdutoSelectModalPacote
                        onFocusChange={setPacoteEmFoco}
                        onSelect={(pacote) => {
                            setPacoteBarra(pacote);
                            const loteAutomatico = getProdutoSelectLoteAutomatico(produto);
                            setLote(loteAutomatico);
                            setLoteEmFoco(null);
                            setLocalEstoque(null);
                            if (temLote) {
                                setEtapa(Step.Lote);
                            } else {
                                selecionarEstoqueOuConcluir(pacote, loteAutomatico);
                            }
                        }}
                        produto={produto}
                        tabelaPrecoIdpk={tabelaPrecoIdpk}
                    />
                )}

                {etapa === Step.Lote && (
                    <ProdutoSelectModalLote
                        onFocusChange={setLoteEmFoco}
                        onSelect={(selecionado) => {
                            setLote(selecionado);
                            setLocalEstoque(null);
                            selecionarEstoqueOuConcluir(pacoteBarra, selecionado);
                        }}
                        produto={produto}
                    />
                )}

                {etapa === Step.LocalEstoque && (
                    <ProdutoSelectModalLocal
                        estoques={estoques}
                        localSelecionado={localSelecionado}
                        onSelect={(estoque) => {
                            setLocalEstoque(estoque);
                            onSelect({ localEstoque: estoque, lote, pacoteBarra, produto });
                        }}
                    />
                )}
            </div>
        </Modal>
    );
}
